import { apiClient, getAuthToken, type JsonValue } from '@/shared/data/client';
import { mapUploadDto, mapQueryResult } from '@/shared/data/dtoMappers';
import type { UploadDto, QueryFilter, QueryResult, QuerySort } from '@flowqa/shared';

export type UploadCreateInput = {
  source: string;
  fileName: string;
  contentType: string;
  runKey?: string | null;
  buildId?: string | null;
  buildKey?: string | null;
  tags?: string[];
};

export type UploadCreateResponse = { uploadId: string; uploadUrl: string };

type UploadQueryRequest = {
  page: number;
  pageSize: number;
  filters?: QueryFilter[];
  sorts?: QuerySort[];
  search?: string;
  includeDeleted?: boolean;
  includeSummary?: boolean;
  includeSummaryDetails?: boolean;
};

const uploadSortFieldMap: Readonly<Record<string, string>> = {
  id: 'createdAt',
};

const mapUploadSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(uploadSortFieldMap, field)
      ? uploadSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const createUpload = async (
  projectId: string,
  input: UploadCreateInput,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<UploadCreateResponse> => {
  return await apiClient.post<UploadCreateResponse>(`projects/${projectId}/uploads/create`, input, {
    orgId,
    token: tokenOverride ?? getAuthToken(),
  });
};

export const uploadFile = async (
  uploadId: string,
  file: File,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<void> => {
  const contentType = file.type || 'application/octet-stream';
  await apiClient.raw<JsonValue | string>(`uploads/${uploadId}/file`, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType, 'x-file-name': file.name },
    orgId,
    token: tokenOverride ?? getAuthToken(),
  });
};

export const commitUpload = async (
  uploadId: string,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<UploadDto> =>
  mapUploadDto(
    await apiClient.post<UploadDto>(`uploads/${uploadId}/commit`, undefined, {
      orgId,
      token: tokenOverride ?? getAuthToken(),
    }),
  );

export const getUploadById = async (
  uploadId: string,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<UploadDto> =>
  mapUploadDto(
    await apiClient.get<UploadDto>(`uploads/${uploadId}`, {
      orgId,
      token: tokenOverride ?? getAuthToken(),
    }),
  );

export const getUploadArtifactContent = async (
  uploadId: string,
  artifactId: string,
  orgId?: string | null,
): Promise<string> => {
  const response = await apiClient.raw<JsonValue | string>(
    `uploads/${uploadId}/artifacts/${artifactId}`,
    { method: 'GET', orgId },
  );
  if (typeof response === 'string') {
    return response;
  }
  return JSON.stringify(response, null, 2);
};

export const queryUploads = async (
  input: {
    page: number;
    pageSize: number;
    filters?: QueryFilter[];
    sorts?: QuerySort[];
    search?: string;
    includeSummary?: boolean;
    includeSummaryDetails?: boolean;
  },
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<QueryResult<UploadDto>> => {
  const includeSummary = input.includeSummary;
  const includeSummaryDetails = input.includeSummaryDetails;
  const payload: UploadQueryRequest = {
    page: input.page,
    pageSize: input.pageSize,
    filters: input.filters,
    sorts: input.sorts ? mapUploadSorts(input.sorts) : undefined,
    search: input.search,
    includeSummary,
    includeSummaryDetails,
  };
  const token = tokenOverride ?? getAuthToken();
  const result = await apiClient.post<QueryResult<UploadDto>>('uploads/query', payload, {
    orgId,
    token,
  });
  return mapQueryResult(result, mapUploadDto);
};
